import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Clock, Gavel, Plus, Scale, X } from 'lucide-react';
import { ApiClientError, EmptyState, Money, Spinner, enumLabel, relativeTime } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Dispute control
 *
 *  Raising a dispute here moves real money out of a merchant's available
 *  balance and into a held bucket, so the form asks for the payment reference
 *  rather than offering a merchant picker: you dispute a transaction, not a
 *  company, and typing the reference is a deliberate act.
 *
 *  Resolving is the other half. WON and WITHDRAWN release the hold; LOST sends
 *  the money out and is the only outcome that demands a written reason.
 * ========================================================================== */

interface Dispute {
  id: string;
  status: 'OPEN' | 'EVIDENCE_REQUIRED' | 'UNDER_REVIEW' | 'WON' | 'LOST' | 'WITHDRAWN';
  category: string;
  reason_code: string | null;
  amount: { amount: string; currency: string; formatted: string };
  payment_id: string | null;
  payment_method: string | null;
  evidence: { narrative: string | null; submitted_at: string | null };
  evidence_due_at: string | null;
  days_to_respond: number | null;
  resolution_note: string | null;
  merchant: { ref: string; name: string } | null;
  assignee: string | null;
  created_at: string;
  resolved_at: string | null;
}

const CATEGORIES = [
  'FRAUD',
  'SERVICE_NOT_RENDERED',
  'DUPLICATE',
  'PRODUCT_NOT_RECEIVED',
  'AUTHORIZATION',
  'PROCESSING_ERROR',
  'OTHER',
] as const;

const FILTERS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'EVIDENCE_REQUIRED', label: 'Awaiting merchant' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
  { value: 'ALL', label: 'All' },
] as const;

const CLOSED = ['WON', 'LOST', 'WITHDRAWN'];

export function Disputes() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('OPEN');
  const [raising, setRaising] = useState(false);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const disputes = useQuery({
    queryKey: ['admin-disputes', status],
    queryFn: () => api.get<{ data: Dispute[] }>(`/disputes?status=${status}`),
    refetchInterval: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-disputes'] });

  const resolve = useMutation({
    mutationFn: (v: { id: string; outcome: 'WON' | 'LOST' | 'WITHDRAWN' }) =>
      api.post(`/disputes/${v.id}/resolve`, { outcome: v.outcome, note: note.trim() || undefined }),
    onSuccess: () => {
      setResolveId(null);
      setNote('');
      setError(null);
      void invalidate();
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not close that dispute.'),
  });

  const rows = disputes.data?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Disputes</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
            Raising a dispute holds the amount out of the merchant's available balance immediately and starts their
            response clock. Closing it releases the hold, or sends the money back to the issuer.
          </p>
        </div>
        {!raising && (
          <button type="button" onClick={() => { setRaising(true); setError(null); }} className="btn btn-primary">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Raise a dispute
          </button>
        )}
      </div>

      {raising && (
        <RaiseDispute
          onClose={() => setRaising(false)}
          onRaised={() => {
            setRaising(false);
            setError(null);
            void invalidate();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-line pb-3">
        <div className="flex rounded-md border border-line bg-surface-inset p-0.5" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              aria-pressed={status === f.value}
              className={`rounded px-3 py-1.5 text-2xs font-medium transition-colors ${
                status === f.value ? 'bg-surface-overlay text-content' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {disputes.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : disputes.isError ? (
        <p role="alert" className="rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          Could not load disputes.{' '}
          {disputes.error instanceof ApiClientError ? disputes.error.message : 'The request failed.'}
        </p>
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState title="No disputes match this filter" description="Raise one against a captured payment above." />
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((dispute) => {
            const open = resolveId === dispute.id;
            const closed = CLOSED.includes(dispute.status);
            return (
              <li key={dispute.id} className="rounded-lg border border-line bg-surface-raised">
                <div className="flex flex-wrap items-start gap-4 p-4 sm:p-5">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface-inset">
                    <Gavel className="h-4 w-4 text-content-subtle" aria-hidden="true" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Money amount={dispute.amount.amount} currency={dispute.amount.currency} className="text-md font-semibold" />
                      <span
                        className={`badge ${
                          dispute.status === 'WON'
                            ? 'badge-success'
                            : dispute.status === 'LOST'
                              ? 'badge-danger'
                              : dispute.status === 'UNDER_REVIEW'
                                ? 'badge-info'
                                : 'badge-warning'
                        }`}
                      >
                        {enumLabel(dispute.status)}
                      </span>
                      {!closed && dispute.days_to_respond !== null && (
                        <span
                          className={`flex items-center gap-1.5 text-xs ${
                            dispute.days_to_respond < 0 ? 'text-danger-text' : 'text-content-subtle'
                          }`}
                        >
                          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                          {dispute.days_to_respond < 0
                            ? `${Math.abs(dispute.days_to_respond)}d overdue`
                            : `${dispute.days_to_respond}d to respond`}
                        </span>
                      )}
                    </div>

                    <p className="mt-2 text-xs text-content-subtle">
                      <span className="text-content">{dispute.merchant?.name ?? '—'}</span>
                      {dispute.merchant && <span className="numeral"> · {dispute.merchant.ref}</span>}
                      {' · '}
                      {enumLabel(dispute.category)}
                      {dispute.reason_code && ` · ${dispute.reason_code}`}
                      {' · '}
                      <span className="numeral">{dispute.payment_id}</span>
                      {' · '}
                      {relativeTime(dispute.created_at)}
                    </p>

                    {dispute.evidence.narrative && (
                      <details className="mt-3" open={dispute.status === 'UNDER_REVIEW'}>
                        <summary className="cursor-pointer text-xs text-brand-400">
                          Merchant's response · {relativeTime(dispute.evidence.submitted_at)}
                        </summary>
                        <p className="mt-2 whitespace-pre-wrap rounded-md border border-line bg-surface-inset px-3.5 py-3 text-xs leading-relaxed text-content-muted">
                          {dispute.evidence.narrative}
                        </p>
                      </details>
                    )}

                    {dispute.resolution_note && (
                      <p className="mt-3 text-xs leading-relaxed text-content-subtle">
                        <span className="text-content-faint">Resolution:</span> {dispute.resolution_note}
                      </p>
                    )}
                  </div>

                  {!closed && !open && (
                    <button
                      type="button"
                      onClick={() => { setResolveId(dispute.id); setNote(''); setError(null); }}
                      className="btn btn-secondary btn-sm shrink-0"
                    >
                      <Scale className="h-3.5 w-3.5" aria-hidden="true" />
                      Close case
                    </button>
                  )}
                </div>

                {open && (
                  <div className="border-t border-line bg-surface-inset/40 p-4 sm:p-5">
                    <label htmlFor={`note-${dispute.id}`} className="label">
                      Resolution note
                    </label>
                    <textarea
                      id={`note-${dispute.id}`}
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Required when losing — the merchant sees this text."
                      className="input h-auto py-2"
                    />

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
                      <button type="button" onClick={() => setResolveId(null)} className="btn btn-ghost btn-sm">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => resolve.mutate({ id: dispute.id, outcome: 'WITHDRAWN' })}
                        disabled={resolve.isPending}
                        className="btn btn-secondary btn-sm"
                      >
                        Withdrawn
                      </button>
                      <button
                        type="button"
                        onClick={() => resolve.mutate({ id: dispute.id, outcome: 'LOST' })}
                        disabled={resolve.isPending || note.trim().length < 10}
                        className="btn btn-sm border-danger-ring bg-danger-soft text-danger-text hover:bg-danger-soft disabled:opacity-50"
                      >
                        Lost · money leaves
                      </button>
                      <button
                        type="button"
                        onClick={() => resolve.mutate({ id: dispute.id, outcome: 'WON' })}
                        disabled={resolve.isPending}
                        className="btn btn-primary btn-sm"
                      >
                        {resolve.isPending ? <Spinner /> : null}
                        Won · release hold
                      </button>
                    </div>
                    {note.trim().length > 0 && note.trim().length < 10 && (
                      <p className="mt-2 text-right text-2xs text-content-faint">
                        A lost dispute needs at least 10 characters of explanation.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Raise
 * ------------------------------------------------------------------------ */

function RaiseDispute({ onClose, onRaised }: { onClose: () => void; onRaised: () => void }) {
  const [form, setForm] = useState({
    payment_id: '',
    category: 'FRAUD',
    reason_code: '',
    amount: '',
    respond_within_days: '7',
    note: '',
  });
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const raise = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/disputes', {
        payment_id: form.payment_id.trim(),
        category: form.category,
        reason_code: form.reason_code.trim() || undefined,
        amount: form.amount.trim() || undefined,
        respond_within_days: Number(form.respond_within_days),
        note: form.note.trim() || undefined,
      }),
    onSuccess: onRaised,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not raise the dispute.'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        raise.mutate();
      }}
      className="rounded-lg border border-line bg-surface-raised p-5"
    >
      <div className="mb-5 flex items-start justify-between gap-4 border-b border-line pb-4">
        <div>
          <h3 className="text-sm font-medium text-content">Raise a dispute</h3>
          <p className="mt-1 max-w-xl text-xs leading-relaxed text-content-subtle">
            Against a captured payment. The amount is held from the merchant's available balance the moment you
            submit, and they are notified with the deadline you set here.
          </p>
        </div>
        <button type="button" onClick={onClose} className="btn btn-ghost h-7 w-7 p-0" aria-label="Cancel">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="d-payment" className="label">
            Payment reference
          </label>
          <input
            id="d-payment"
            required
            value={form.payment_id}
            onChange={set('payment_id')}
            placeholder="pay_R7KQ2M4X9B1T"
            className="input numeral"
          />
          <p className="mt-1.5 text-2xs text-content-faint">The payment being claimed back. It must be captured.</p>
        </div>

        <div>
          <label htmlFor="d-category" className="label">
            Category
          </label>
          <select id="d-category" value={form.category} onChange={set('category')} className="input">
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {enumLabel(c)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="d-reason" className="label">
            Issuer reason code (optional)
          </label>
          <input id="d-reason" value={form.reason_code} onChange={set('reason_code')} placeholder="10.4" className="input numeral" />
        </div>

        <div>
          <label htmlFor="d-amount" className="label">
            Amount in ₹ (optional)
          </label>
          <input id="d-amount" value={form.amount} onChange={set('amount')} placeholder="Full payment" className="input numeral" />
          <p className="mt-1.5 text-2xs text-content-faint">Leave blank to dispute the whole captured amount.</p>
        </div>

        <div>
          <label htmlFor="d-days" className="label">
            Merchant has (days)
          </label>
          <input
            id="d-days"
            type="number"
            min={1}
            max={45}
            value={form.respond_within_days}
            onChange={set('respond_within_days')}
            className="input numeral"
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="d-note" className="label">
            Internal note (optional)
          </label>
          <input id="d-note" value={form.note} onChange={set('note')} className="input" placeholder="Cardholder reports an unauthorised transaction." />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs leading-relaxed text-danger-text">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <button type="button" onClick={onClose} className="btn btn-ghost">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={raise.isPending || form.payment_id.trim().length < 4}>
          {raise.isPending ? <Spinner /> : <Gavel className="h-4 w-4" aria-hidden="true" />}
          Raise and hold the funds
        </button>
      </div>
    </form>
  );
}
